type StorageName = 'localStorage' | 'sessionStorage';

type SafeStorage = {
  getItem: (key: string) => string | null;
  /** `false` when the write was dropped, for callers that must not proceed. */
  setItem: (key: string, value: string) => boolean;
  removeItem: (key: string) => void;
  /** Every key currently held, or `null` when storage could not be read. */
  keys: () => string[] | null;
  /**
   * Whether a value written now could be read back later — probed as a real
   * write/read/remove round trip. Quota-exhausted storage reads fine and
   * refuses writes; a denied store can refuse either half. Use only where
   * "cannot persist" has to be told apart from "not set".
   */
  canPersist: () => boolean;
};

/** Written and removed by `canPersist`; never left behind. */
const PERSISTENCE_PROBE_KEY = 'moderne-storage-probe';
const PERSISTENCE_PROBE_VALUE = '1';

const loggedFailures = new Set<string>();

const warnOnce = (
  name: StorageName,
  operation: string,
  key: string | undefined,
  error: unknown
): void => {
  // Read `.name` structurally rather than via `instanceof Error`: a
  // `DOMException` raised by another realm (an iframe, or jsdom under test)
  // fails that check, which would collapse every error into one dedupe key.
  const errorName =
    typeof error === 'object' && error !== null && 'name' in error
      ? String((error as { name: unknown }).name)
      : 'Error';
  // Keyed by error name too: a quota failure and a denied access are different
  // conditions, and collapsing them hides the second one for the page lifetime.
  const dedupeKey = `${name}.${operation}.${errorName}`;
  if (loggedFailures.has(dedupeKey)) {
    return;
  }
  loggedFailures.add(dedupeKey);
  const target = key
    ? `${name}.${operation}("${key}")`
    : `${name}.${operation}`;
  // biome-ignore lint/suspicious/noConsole: single log site for storage degradation
  console.warn(`Browser storage: ${target} failed with ${errorName}.`, error);
};

const createSafeStorage = (name: StorageName): SafeStorage => {
  /**
   * `window.localStorage` throws `SecurityError` on the *property access* in a
   * sandboxed iframe, with cookies blocked, or under an enterprise storage
   * policy — so `globalThis?.window?.localStorage` is itself the throwing
   * expression and cannot guard anything. Reading it inside the `try` is what
   * makes this safe; the method call can throw separately (quota exceeded).
   */
  const attempt = <T>(
    operation: string,
    key: string | undefined,
    action: (storage: Storage) => T,
    fallback: T
  ): T => {
    try {
      const storage = globalThis.window?.[name];
      if (!storage) {
        return fallback;
      }
      return action(storage);
    } catch (error) {
      warnOnce(name, operation, key, error);
      return fallback;
    }
  };

  return {
    getItem: (key) =>
      attempt('getItem', key, (storage) => storage.getItem(key), null),
    setItem: (key, value) =>
      attempt(
        'setItem',
        key,
        (storage) => {
          storage.setItem(key, value);
          return true;
        },
        false
      ),
    removeItem: (key) =>
      attempt(
        'removeItem',
        key,
        (storage) => storage.removeItem(key),
        undefined
      ),
    keys: () =>
      attempt('keys', undefined, (storage) => Object.keys(storage), null),
    canPersist: () =>
      attempt(
        'canPersist',
        PERSISTENCE_PROBE_KEY,
        (storage) => {
          storage.setItem(PERSISTENCE_PROBE_KEY, PERSISTENCE_PROBE_VALUE);
          // Read back rather than trusting the write: a store whose reads throw
          // could accept the flag and never return it, so the caller would
          // re-prompt on every visit — the outcome the probe exists to prevent.
          const readBack = storage.getItem(PERSISTENCE_PROBE_KEY);
          storage.removeItem(PERSISTENCE_PROBE_KEY);
          return readBack === PERSISTENCE_PROBE_VALUE;
        },
        false
      )
  };
};

export const safeLocalStorage = createSafeStorage('localStorage');
export const safeSessionStorage = createSafeStorage('sessionStorage');

/**
 * Gets item from local storage and parses it for valid JSON
 *
 * @param storageItem - the key local storage uses
 * @param fallBack - if there was an error the fallback will be used
 */
export const getObjectFromLocalStorage = <T>(
  storageItem: string,
  fallBack?: T
): T => {
  const persistedItemString = safeLocalStorage.getItem(storageItem);

  if (!persistedItemString) {
    return fallBack;
  }

  try {
    return JSON.parse(persistedItemString);
  } catch {
    return fallBack;
  }
};
